import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Pencil, Trash2, Webhook, X } from "lucide-react";
import {
  getDiscordWebhooks,
  createDiscordWebhook,
  updateDiscordWebhook,
  deleteDiscordWebhook,
  type DiscordWebhook,
  type DiscordWebhookInput,
} from "../../services/DiscordWebhookService";

const emptyForm = (): DiscordWebhookInput => ({ name: "", url: "" });

type FormMode = { type: "closed" } | { type: "create" } | { type: "edit"; webhook: DiscordWebhook };

/** Show enough of the URL to recognize it without pasting the full token in the table. */
function maskWebhookUrl(url: string): string {
  const match = url.match(/webhooks\/(\d+)\//);
  return match ? `…/webhooks/${match[1]}/•••` : url.slice(0, 40) + (url.length > 40 ? "…" : "");
}

function formatCreated(createdAt?: number): string {
  if (!createdAt) return "—";
  return new Date(createdAt).toLocaleDateString();
}

export default function DiscordWebhooksTab() {
  const queryClient = useQueryClient();
  const [formMode, setFormMode] = useState<FormMode>({ type: "closed" });
  const [form, setForm] = useState<DiscordWebhookInput>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const editTriggerRef = useRef<HTMLButtonElement | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-discord-webhooks"],
    queryFn: getDiscordWebhooks,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-discord-webhooks"] });
  const closeForm = useCallback(() => {
    setFormMode({ type: "closed" });
    setForm(emptyForm());
    setFormError(null);
  }, []);

  useEffect(() => {
    if (formMode.type !== "edit") return;

    const previousOverflow = document.body.style.overflow;
    const focusFrame = window.requestAnimationFrame(() => nameInputRef.current?.focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeForm();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      editTriggerRef.current?.focus();
      editTriggerRef.current = null;
    };
  }, [closeForm, formMode.type]);

  const createMutation = useMutation({
    mutationFn: createDiscordWebhook,
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (err: Error) => setFormError(err.message || "Failed to create webhook"),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: DiscordWebhookInput }) => updateDiscordWebhook(id, input),
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (err: Error) => setFormError(err.message || "Failed to update webhook"),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteDiscordWebhook,
    // Deleting also clears references from keyword searches server-side.
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin-keyword-searches"] });
    },
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const webhooks = data?.webhooks ?? [];

  const openCreate = () => { setForm(emptyForm()); setFormError(null); setFormMode({ type: "create" }); };
  const openEdit = (w: DiscordWebhook, trigger: HTMLButtonElement) => {
    editTriggerRef.current = trigger;
    setForm({ name: w.name, url: w.url });
    setFormError(null);
    setFormMode({ type: "edit", webhook: w });
  };

  const submit = () => {
    if (!form.name.trim()) { setFormError("Name is required"); return; }
    if (!form.url.trim()) { setFormError("Webhook URL is required"); return; }
    if (!/^https:\/\/(ptb\.|canary\.)?discord(app)?\.com\/api\/webhooks\//.test(form.url.trim())) {
      setFormError("Must be a Discord webhook URL (https://discord.com/api/webhooks/...)");
      return;
    }
    const input = { name: form.name.trim(), url: form.url.trim() };
    if (formMode.type === "create") createMutation.mutate(input);
    else if (formMode.type === "edit") updateMutation.mutate({ id: formMode.webhook.id, input });
  };

  const handleDelete = (w: DiscordWebhook) => {
    if (confirm(`Delete webhook "${w.name}"? Keyword searches notifying it will stop sending notifications.`)) {
      deleteMutation.mutate(w.id);
    }
  };

  const formContent = formMode.type !== "closed" ? (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 id="discord-webhook-form-title" className="text-[13px] font-semibold text-gray-900 dark:text-white">
          {formMode.type === "create" ? "New Discord webhook" : `Edit "${formMode.webhook.name}"`}
        </h3>
        <button type="button" onClick={closeForm} className="p-1 text-gray-400 hover:text-gray-900 dark:hover:text-white" aria-label="Close form">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
        <div className="sm:col-span-4">
          <label htmlFor="discord-webhook-name" className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Name</label>
          <input
            ref={formMode.type === "edit" ? nameInputRef : undefined}
            id="discord-webhook-name"
            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            value={form.name}
            placeholder="e.g. Deep deals channel"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div className="sm:col-span-8">
          <label htmlFor="discord-webhook-url" className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Webhook URL</label>
          <input
            id="discord-webhook-url"
            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
            value={form.url}
            placeholder="https://discord.com/api/webhooks/…"
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
          />
        </div>
      </div>

      {formError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{formError}</p>}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40"
        >
          {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {formMode.type === "create" ? "Create" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={closeForm}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700"
        >
          Cancel
        </button>
      </div>
    </form>
  ) : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">Discord webhooks</h2>
          <p className="text-xs text-gray-400 dark:text-dark-400 mt-0.5">
            Named webhooks that keyword searches can notify when a deep-discount deal is found.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90"
        >
          <Plus className="w-3.5 h-3.5" /> Add webhook
        </button>
      </div>

      {formMode.type === "create" && (
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm p-4">
          {formContent}
        </div>
      )}

      {formMode.type === "edit" && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4 dark:bg-black/70 sheet-backdrop-in"
          onMouseDown={(event) => event.target === event.currentTarget && closeForm()}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="discord-webhook-form-title"
            className="w-full rounded-t-2xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-dark-700 dark:bg-dark-800 sm:max-w-2xl sm:rounded-2xl sheet-panel-in-sm"
          >
            {formContent}
          </section>
        </div>,
        document.body,
      )}

      {webhooks.length === 0 ? (
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm py-14 flex flex-col items-center text-center">
          <Webhook className="w-8 h-8 text-gray-300 dark:text-dark-500 mb-2" />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No Discord webhooks yet</p>
          <p className="text-xs text-gray-400 dark:text-dark-400 mt-1">Add a webhook so keyword searches can send deal notifications.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-100 dark:border-dark-700">
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Name</th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Webhook URL</th>
                <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Created</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {webhooks.map((w) => (
                <tr key={w.id} className="border-b border-gray-50 dark:border-dark-700/60 last:border-0">
                  <td className="px-4 py-3 text-[13px] font-medium text-gray-900 dark:text-white whitespace-nowrap">{w.name}</td>
                  <td className="px-4 py-3 text-[12px] font-mono text-gray-500 dark:text-dark-300 whitespace-nowrap">{maskWebhookUrl(w.url)}</td>
                  <td className="px-4 py-3 text-[13px] text-gray-600 dark:text-dark-300 whitespace-nowrap">{formatCreated(w.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(event) => openEdit(w, event.currentTarget)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700"
                        aria-label={`Edit ${w.name}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(w)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                        aria-label={`Delete ${w.name}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
